import React from 'react'
import {Heart, Share2} from 'lucide-react'
import AddToCartButton from '../../../components/AddToCartButton'
import type { ProductV2Detail, Lang } from './types'

interface ProductInfoV2Props {
  product: ProductV2Detail
  currentLang: Lang
  quantity: number
  onQuantityChange: (q: number) => void
  isWishlisted: boolean
  favoriteLoading: boolean
  onToggleWishlist: () => void
  onShare: () => void
}

/**
 * ProductInfoV2
 *
 * Right-side sticky info column (Barista Map style):
 *   - Brand line ("ONGU Studio")
 *   - Serif H1 product title
 *   - Price
 *   - Variant select (weight) — disabled when only one option
 *   - Quantity stepper
 *   - Black solid "Add to cart" CTA full-width
 *   - Minimal wishlist + share underline links
 *
 * No icons clutter, no flavour-note pills here — those live in
 * ProductDescriptionV2 / ProductSpecsAccordionV2 to keep the buy box clean.
 */
const ProductInfoV2: React.FC<ProductInfoV2Props> = ({
  product,
  currentLang,
  quantity,
  onQuantityChange,
  isWishlisted,
  favoriteLoading,
  onToggleWishlist,
  onShare,
}) => {
  const isZh = currentLang === 'zh'
  const displayName = isZh ? product.name : (product.nameEn || product.name)

  const formatPrice = (n: number) =>
    Math.floor(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const weightLabel = product.specifications?.weight || (isZh ? '227g' : '227g')

  return (
    <div className="flex flex-col gap-7">
      {/* Brand eyebrow */}
      <p
        className="text-[11px] uppercase text-black/50"
        style={{ letterSpacing: '0.25em', fontFamily: "'EB Garamond', serif" }}
      >
        ONGU Studio
      </p>

      {/* Title — serif, large */}
      <h1
        className="text-3xl md:text-[40px] leading-[1.15] text-black"
        style={{
          fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
          fontWeight: 400,
          letterSpacing: '0.01em',
        }}
      >
        {displayName}
      </h1>

      {/* Price */}
      <div className="flex items-baseline gap-3">
        <span
          className="text-2xl text-black"
          style={{ fontFamily: "'EB Garamond', serif", fontWeight: 400 }}
        >
          NT$ {formatPrice(Number(product.price))}
        </span>
        {product.originalPrice && product.originalPrice > product.price && (
          <span
            className="text-base text-black/40 line-through"
            style={{ fontFamily: "'EB Garamond', serif" }}
          >
            NT$ {formatPrice(Number(product.originalPrice))}
          </span>
        )}
      </div>

      {/* Weight label (read-only single variant) */}
      <div className="flex flex-col gap-2">
        <label
          className="text-[11px] uppercase text-black/50"
          style={{ letterSpacing: '0.2em' }}
        >
          {isZh ? '重量' : 'Weight'}
        </label>
        <div
          className="flex h-11 items-center justify-between border border-black/15 bg-white px-4 text-sm text-black"
          style={{ fontFamily: "'EB Garamond', serif" }}
        >
          <span>{weightLabel}</span>
          <span className="text-black/40 text-xs">
            {isZh ? '單一規格' : 'One size'}
          </span>
        </div>
      </div>

      {/* Quantity */}
      <div className="flex flex-col gap-2">
        <label
          className="text-[11px] uppercase text-black/50"
          style={{ letterSpacing: '0.2em' }}
        >
          {isZh ? '數量' : 'Quantity'}
        </label>
        <div className="inline-flex h-11 w-fit items-center border border-black/15 bg-white">
          <button
            type="button"
            onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            className="h-full w-11 text-base text-black/60 hover:text-black"
            aria-label="Decrease quantity"
          >
            −
          </button>
          <span
            className="min-w-[44px] text-center text-sm text-black"
            style={{ fontFamily: "'EB Garamond', serif" }}
          >
            {quantity}
          </span>
          <button
            type="button"
            onClick={() => onQuantityChange(quantity + 1)}
            className="h-full w-11 text-base text-black/60 hover:text-black"
            aria-label="Increase quantity"
          >
            +
          </button>
        </div>
      </div>

      {/* Add to cart — black solid full-width */}
      <div className="pt-2">
        <AddToCartButton
          product={product as any}
          quantity={quantity}
          showQuantity={false}
          size="lg"
          variant="primary"
          className="!h-12 !w-full !rounded-none !bg-black hover:!bg-black/85 !text-white !text-[13px] !tracking-[0.25em] !uppercase !border-0"
        />
      </div>

      {/* Stock note */}
      <p
        className="-mt-3 text-[11px] text-black/40"
        style={{ letterSpacing: '0.15em', fontFamily: "'EB Garamond', serif" }}
      >
        {product.inventory > 0
          ? isZh
            ? `庫存 ${product.inventory} · 出貨前 3–7 天烘焙`
            : `In stock · Roasted 3–7 days before shipping`
          : isZh
            ? '目前缺貨'
            : 'Currently out of stock'}
      </p>

      {/* Wishlist + share — minimal underline links */}
      <div className="flex items-center gap-6 pt-1">
        <button
          onClick={onToggleWishlist}
          disabled={favoriteLoading}
          className={`group flex items-center gap-2 text-[11px] uppercase transition-colors ${
            isWishlisted ? 'text-red-600' : 'text-black/60 hover:text-black'
          }`}
          style={{ letterSpacing: '0.2em', fontFamily: "'EB Garamond', serif" }}
        >
          <Heart size={14} strokeWidth={1.5} className={isWishlisted ? 'fill-current' : ''} />
          <span className="border-b border-current pb-0.5">
            {isZh
              ? isWishlisted ? '已收藏' : '加入收藏'
              : isWishlisted ? 'Saved' : 'Save'}
          </span>
        </button>

        <button
          onClick={onShare}
          className="group flex items-center gap-2 text-[11px] uppercase text-black/60 hover:text-black transition-colors"
          style={{ letterSpacing: '0.2em', fontFamily: "'EB Garamond', serif" }}
        >
          <Share2 size={14} strokeWidth={1.5} />
          <span className="border-b border-current pb-0.5">
            {isZh ? '分享' : 'Share'}
          </span>
        </button>
      </div>
    </div>
  )
}

export default ProductInfoV2
